import { useState, useRef, useEffect } from 'react';
import { connectLaser, disconnectLaser } from './services/laserService';
import appConfig from './appConfig';
import { supabase } from './supabase';
import ExcelJS from 'exceljs';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { fmtDate } from './formatDate'; // PATCH oct21-ui-measure

// Quote the search text so a comma or bracket (for example Smith, John) does not break the filter
function orLikeQ(col, term) {
  var t = String(term).trim().replace(/\s+/g, ' ').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return col + '.ilike."%' + t + '%"';
}

var FRACS     = ['', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'];
var FRAC_VALS = { '':0, '1/8':0.125, '1/4':0.25, '3/8':0.375, '1/2':0.5, '5/8':0.625, '3/4':0.75, '7/8':0.875 };
var START_ROWS = 10; // PATCH oct21-ui-measure: start with 10 rows, Add window adds more

function roundUp6(v)  { return v <= 0 ? 0 : Math.ceil(v / 6)  * 6; }
function roundUp12(v) { return v <= 0 ? 0 : Math.ceil(v / 12) * 12; }

function calcSqFt(w, h, mode, minS) { // PATCH oct20-minsqft
  var wWhole = Math.floor(w);
  var hWhole = Math.floor(h);
  var rw = mode === '1foot' ? roundUp12(wWhole) : roundUp6(wWhole);
  var rh = mode === '1foot' ? roundUp12(hWhole) : roundUp6(hWhole);
  if (rw === 0 || rh === 0) return { sqft: 0, rw, rh, isMin: false };
  var raw  = (rw * rh) / 144;
  var sqft = mode === '1foot' ? Math.ceil(raw) : Math.ceil(raw / 0.5) * 0.5;
  var mn = minS == null ? 9 : minS;
  var isMin = mn > 0 && sqft < mn;
  return { sqft: isMin ? mn : sqft, rw, rh, isMin };
}

function toFracLabel(decimal) {
  var whole = Math.floor(decimal);
  var frac  = decimal - whole;
  var map   = { 0:'', 0.125:'⅛', 0.25:'¼', 0.375:'⅜', 0.5:'½', 0.625:'⅝', 0.75:'¾', 0.875:'⅞' };
  var key   = Math.round(frac * 1000) / 1000;
  return whole + (map[key] ? ' ' + map[key] : '') + '"';
}

function makeRows(n) {
  var rows = [];
  for (var i = 0; i < (n || START_ROWS); i++) rows.push({ id: i + 1, w: '', wf: '', h: '', hf: '' });
  return rows;
}

// Reconstruct rows from a saved measurement record, preserving original row positions
function rowsFromRecord(record) {
  var maxNum = 0;
  ((record && record.rows) || []).forEach(function(r) { if ((r.num || 0) > maxNum) maxNum = r.num; });
  var filled = makeRows(Math.max(START_ROWS, maxNum));
  if (record && record.rows && record.rows.length > 0) {
    record.rows.forEach(function(r) {
      var idx = (r.num || 1) - 1; // num is 1-indexed
      if (idx >= 0 && idx < filled.length) {
        filled[idx] = {
          id: r.num,
          w:  r.width_whole  != null ? String(r.width_whole)  : '',
          wf: r.width_frac   || '',
          h:  r.height_whole != null ? String(r.height_whole) : '',
          hf: r.height_frac  || '',
        };
      }
    });
  }
  return filled;
}

function CustomerSearch({ onSelect, theme, C }) {
  // PATCH oct21-ui-measure: new search box style
  var [query,   setQuery]   = useState('');
  var [results, setResults] = useState([]);

  async function search(val) {
    setQuery(val);
    if (!val.trim()) { setResults([]); return; }
    var { data } = await supabase.from('customers')
      .select('id, full_name, phone, area')
      .eq('company_id', appConfig.companyId)
      .or(orLikeQ('full_name', val) + ',' + orLikeQ('phone', val))
      .limit(6);
    setResults(data || []);
  }

  var text = theme ? theme.text : '#1C2B2A';
  var muted = theme ? theme.mutedText : '#5F6B6A';
  var border = theme ? theme.cardBorder : '#E2E7E6';

  return (
    <div style={{ position: 'relative', marginBottom: 12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, minHeight:44, background: theme ? theme.inputBg : 'white', border:'1px solid '+(theme ? theme.inputBorder : '#CBD3D2'), borderRadius:10, padding:'0 6px 0 12px', boxSizing:'border-box' }}>
        <span style={{ color: muted }}><MIcon name="search" size={18} /></span>
        <input id="m-customer" placeholder="Name or phone" value={query} onChange={e => search(e.target.value)}
          style={{ flex:1, minWidth:0, border:'none', outline:'none', fontSize:15, background:'transparent', color: text, fontFamily:'inherit', minHeight:42 }} />
        {query && (
          <button aria-label="Clear search" onClick={() => { setQuery(''); setResults([]); }} style={{ width:36, height:36, background:'transparent', border:'none', color: muted, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <MIcon name="x" size={16} />
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background: theme ? theme.card : 'white', border:'1px solid '+border, borderRadius:10, zIndex:999, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', marginTop:4, overflow:'hidden' }}>
          {results.map(cu => (
            <button key={cu.id} onClick={() => { onSelect(cu); setQuery(''); setResults([]); }}
              style={{ width:'100%', padding:'10px 14px', border:'none', borderBottom:'1px solid '+border, background:'transparent', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', textAlign:'left', fontFamily:'inherit' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color: text }}>{cu.full_name}</div>
                <div style={{ fontSize:13, color: muted }}>{[cu.phone, cu.area].filter(Boolean).join(' · ')}</div>
              </div>
              <span style={{ color: muted }}><MIcon name="chevronRight" size={18} /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// PATCH oct21-ui-measure: line icons (stroke 2, 24 viewBox, currentColor)
var M_ICONS = {
  search: [{ c: [11, 11, 7] }, 'M21 21l-4.35-4.35'],
  x: ['M18 6L6 18', 'M6 6l12 12'],
  chevronRight: ['M9 18l6-6-6-6'],
  arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7'],
  info: [{ c: [12, 12, 9] }, 'M12 16v-4', 'M12 8h.01'],
  plus: ['M12 5v14', 'M5 12h14'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z'],
  bluetooth: ['M6.5 6.5l11 11L12 23V1l5.5 5.5-11 11'],
  sheet: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M8 13h8', 'M8 17h8', 'M12 11v8'],
  whatsapp: ['M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z', 'M9 9.5c.5 2.5 2.5 4.5 5 5'],
};
function MIcon({ name, size }) {
  var parts = M_ICONS[name] || [];
  var s = size || 20;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {parts.map((p, i) => (typeof p === 'string' ? <path key={i} d={p} /> : <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />))}
    </svg>
  );
}

export default function Measurements({ onClose, theme, C, showToast, editingRecord = null, plan = null, minSqft = 9 }) {

  // ── STATE — initialised from editingRecord when editing an existing record ──
  var [rows, setRows] = useState(function() {
    return editingRecord ? rowsFromRecord(editingRecord) : makeRows();
  });
  var rowsLenRef = useRef(0);
  rowsLenRef.current = rows.length;
  function addRow() {
    rowsLenRef.current += 1;
    setRows(prev => prev.concat([{ id: prev.length + 1, w: '', wf: '', h: '', hf: '' }]));
  }
  var [customer, setCustomer] = useState(function() {
    if (editingRecord && editingRecord.contact_id) {
      return { id: editingRecord.contact_id, full_name: editingRecord.customer_name };
    }
    return null;
  });
  var [jobDesc,      setJobDesc]      = useState(editingRecord ? (editingRecord.job_description || '') : '');
  var [saving,       setSaving]       = useState(false);
  var [pricePerSqFt, setPricePerSqFt] = useState(editingRecord && editingRecord.price_per_sqft ? String(editingRecord.price_per_sqft) : '');
  var [roundMode,    setRoundMode]    = useState(editingRecord ? (editingRecord.rounding_mode || '6inch') : '6inch');
  var [laserMode,    setLaserMode]    = useState('single');
  var [activeRow,    setActiveRow]    = useState(0);
  var [laserState,   setLaserState]   = useState(null);
  var [laserConn,    setLaserConn]    = useState(null); // { brand, beta }
  var [laserBusy,    setLaserBusy]    = useState(false);
  var [laserTarget,  setLaserTarget]  = useState({ row: 0, field: 'w' });
  var laserModeRef   = useRef('single');
  var laserTargetRef = useRef({ row: 0, field: 'w' });
  var shotsRef       = useRef([]);
  laserModeRef.current = laserMode;

  // PATCH oct20-save-notice: on screen message, export only after save
  var [notice, setNotice] = useState(null);
  var [dirty, setDirty] = useState(!editingRecord);
  var firstRenderRef = useRef(true);
  var noticeTimerRef = useRef(null);
  useEffect(function() {
    if (firstRenderRef.current) { firstRenderRef.current = false; return; }
    setDirty(true);
  }, [rows, customer, jobDesc, pricePerSqFt, roundMode]);
  function note(text, type) {
    setNotice({ text: text, type: type || 'ok' });
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(function() { setNotice(null); }, 5000);
    try { if (showToast) showToast(text, type === 'error' ? 'error' : undefined); } catch (e) {}
  }
  // Laser is not part of the Starter plan. Trial, Pro and Business can use it.
  // PLAY POLICY: Android wording must not mention plans or upgrading.
  var laserAllowed = plan !== 'starter';
  var laserBlockedMsg = Capacitor.isNativePlatform()
    ? 'Laser measure is not included in your current plan.'
    : 'Laser measure is available on Pro and Business plans.';

  useEffect(function() {
    return function() { disconnectLaser(); };
  }, []);

  var inputRefs = useRef({});
  var timerRef  = useRef({});

  // PATCH oct21-ui-measure: colours (light and dark)
  var dark       = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  var teal       = C ? C.teal : '#2F6F6B';
  var tealText   = dark ? '#7CC2BB' : teal;
  var tealTint   = dark ? '#1C2E2C' : '#E6F0EF';
  var cardBg     = theme ? theme.card        : 'white';
  var sectionBg  = theme ? theme.sectionBg   : '#F7F9F8';
  var textColor  = theme ? theme.text        : '#1C2B2A';
  var subText    = theme ? theme.subText     : '#3A4645';
  var mutedText  = theme ? theme.mutedText   : '#5F6B6A';
  var borderCol  = theme ? theme.cardBorder  : '#E2E7E6';
  var inputBg    = theme ? theme.inputBg     : 'white';
  var inputBorder= theme ? theme.inputBorder : '#CBD3D2';
  var arrowSvg   = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238E9998' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

  var IS  = { minHeight:44, padding:'0 4px', borderRadius:10, border:'1px solid '+inputBorder, fontSize:16, background:inputBg, color:textColor, boxSizing:'border-box', textAlign:'center', width:'100%', minWidth:0, fontFamily:'inherit' };
  var SEL = { minHeight:44, width:54, flexShrink:0, padding:'0 16px 0 6px', borderRadius:10, border:'1px solid '+inputBorder, fontSize:14, background:inputBg+' '+arrowSvg+' no-repeat right 5px center', color:textColor, boxSizing:'border-box', appearance:'none', WebkitAppearance:'none', MozAppearance:'none', fontFamily:'inherit', cursor:'pointer' };
  var FIELD = { width:'100%', minHeight:44, padding:'10px 12px', borderRadius:10, border:'1px solid '+inputBorder, fontSize:15, background:inputBg, color:textColor, boxSizing:'border-box', fontFamily:'inherit' };
  var LBL = { display:'block', fontSize:13, fontWeight:600, color:textColor, marginBottom:6 };
  var CARD = { background:cardBg, border:'1px solid '+borderCol, borderRadius:14, padding:16, marginBottom:12 };
  var BTN_P = { minHeight:44, padding:'0 16px', borderRadius:10, fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:'inherit', background:teal, color:'white', border:'1px solid '+teal, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, boxSizing:'border-box' };
  var BTN_S = Object.assign({}, BTN_P, { background:cardBg, color:textColor, border:'1px solid '+inputBorder });
  var isPhone = Capacitor.isNativePlatform() || (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  var [showTips, setShowTips] = useState(false);

  function getDecimal(row) {
    return {
      w: (parseFloat(row.w) || 0) + (FRAC_VALS[row.wf] || 0),
      h: (parseFloat(row.h) || 0) + (FRAC_VALS[row.hf] || 0),
    };
  }

  // PATCH oct20-minsqft: saved records keep their own minimum (older records used 9)
  var minUsed = editingRecord
    ? (editingRecord.min_sqft != null ? Number(editingRecord.min_sqft) : 9)
    : (minSqft != null && !isNaN(Number(minSqft)) ? Number(minSqft) : 9);

  var calculations = rows.map(function(row) {
    var { w, h } = getDecimal(row);
    if (w === 0 && h === 0) return { sqft:0, rw:0, rh:0, isMin:false, hasData:false };
    var res = calcSqFt(w, h, roundMode, minUsed);
    return Object.assign({}, res, { hasData: w > 0 && h > 0 });
  });

  // PATCH oct20-minsqft: total rounded up to a whole number (122.5 becomes 123)
  var totalSqFt    = Math.ceil(calculations.reduce((s, c) => s + (c.hasData ? c.sqft : 0), 0) - 1e-9);
  var totalWindows = calculations.filter(c => c.hasData).length;
  var totalPrice   = pricePerSqFt && parseFloat(pricePerSqFt) > 0 ? totalSqFt * parseFloat(pricePerSqFt) : 0;

  function handleFocus(e) {
    e.target.select();
    var keys = Object.keys(inputRefs.current);
    for (var i = 0; i < keys.length; i++) {
      if (inputRefs.current[keys[i]] === e.target) {
        var parts = keys[i].split('-');
        if (parts[1] === 'w' || parts[1] === 'h') {
          var t = { row: parseInt(parts[0], 10), field: parts[1] };
          laserTargetRef.current = t;
          setLaserTarget(t);
        }
        break;
      }
    }
  }

  function handleInput(rowIdx, field, value) {
    var key = rowIdx + '-' + field;
    if (timerRef.current[key]) clearTimeout(timerRef.current[key]);
    setRows(prev => prev.map((r, i) => i !== rowIdx ? r : Object.assign({}, r, { [field]: value })));
    if ((field === 'w' || field === 'h') && isPhone) { // PATCH oct23-autojump: auto jump on phone only, computer uses Tab
      timerRef.current[key] = setTimeout(function() {
        if (!value) return;
        var cur = inputRefs.current[key]; // PATCH oct23-measure: only jump if still in this box
        if (!cur || document.activeElement !== cur) return;
        var nextKey = field === 'w' ? rowIdx + '-wf' : rowIdx + '-hf';
        if (inputRefs.current[nextKey]) inputRefs.current[nextKey].focus();
      }, 800);
    }
  }

  // PATCH oct20-minsqft: arrow keys change the fraction and stay put; Enter, mouse or tap moves on
  var fracKeyRef = useRef(false);
  function fracKeyDown(e, rowIdx, field) {
    if (e.key === 'Enter') { e.preventDefault(); fracKeyRef.current = false; fracAdvance(rowIdx, field); return; }
    // PATCH oct23-measure: Tab goes width fraction -> height, height fraction -> next row width
    if (e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) { e.preventDefault(); fracKeyRef.current = false; fracAdvance(rowIdx, field); return; }
    if (e.key === 'Tab' || e.altKey) return;
    fracKeyRef.current = true;
  }
  function fracKeyUp() { setTimeout(function() { fracKeyRef.current = false; }, 0); }
  function fracMouseDown() { fracKeyRef.current = false; }

  function handleFracChange(rowIdx, field, value) {
    setRows(prev => prev.map((r, i) => i !== rowIdx ? r : Object.assign({}, r, { [field]: value })));
    if (fracKeyRef.current) return;
    fracAdvance(rowIdx, field);
  }

  function fracAdvance(rowIdx, field) {
    // PATCH oct21-ui-measure: Enter on the last row adds a new row
    var nextKey;
    if (field === 'wf') {
      nextKey = rowIdx + '-h';
    } else {
      if (rowIdx + 1 >= rowsLenRef.current) addRow();
      nextKey = (rowIdx + 1) + '-w';
    }
    setTimeout(() => {
      if (inputRefs.current[nextKey]) { inputRefs.current[nextKey].focus(); inputRefs.current[nextKey].select(); }
    }, 80);
    if (field === 'hf') setActiveRow(rowIdx + 1);
  }

  function clearRow(rowIdx) {
    setRows(prev => prev.map((r, i) => i !== rowIdx ? r : { id: r.id, w:'', wf:'', h:'', hf:'' }));
  }

  function shotsNeeded() { return laserMode === 'single' ? 1 : 3; }

  // ── LASER (Bluetooth) ──
  function laserToast(msg) { if (showToast) showToast(msg); }

  function fillFromLaser(inches) {
    var t = laserTargetRef.current;
    var eighths = Math.floor(inches * 8 + 0.001); // round DOWN to nearest 1/8 inch
    var whole = Math.floor(eighths / 8);
    var frac = FRACS[eighths % 8];
    var fracField = t.field === 'w' ? 'wf' : 'hf';
    setRows(prev => prev.map((r, i) => i !== t.row ? r : Object.assign({}, r, { [t.field]: String(whole), [fracField]: frac })));
    if (t.field === 'h' && t.row + 1 >= rowsLenRef.current) addRow(); // PATCH oct21-ui-measure
    var next = t.field === 'w' ? { row: t.row, field: 'h' } : { row: t.row + 1, field: 'w' };
    laserTargetRef.current = next;
    setLaserTarget(next);
    setActiveRow(next.row);
  }

  function handleLaserMeasurement(m) {
    var inches = m.meters * 39.3700787;
    var mode = laserModeRef.current;
    var needed = mode === 'single' ? 1 : 3;
    var t = laserTargetRef.current;
    shotsRef.current = shotsRef.current.concat([inches]);
    if (shotsRef.current.length < needed) {
      setLaserState({ rowIdx: t.row, field: t.field, shots: shotsRef.current.slice() });
      return;
    }
    var shots = shotsRef.current;
    var value = mode === 'smallest' ? Math.min.apply(null, shots)
              : mode === 'average'  ? shots.reduce((s, v) => s + v, 0) / shots.length
              : shots[0];
    shotsRef.current = [];
    setLaserState(null);
    fillFromLaser(value);
  }

  async function handleConnectLaser() {
    if (!laserAllowed && !laserConn) { if (showToast) showToast(laserBlockedMsg, 'error'); return; }
    if (laserConn) {
      await disconnectLaser();
      setLaserConn(null);
      shotsRef.current = [];
      setLaserState(null);
      return;
    }
    setLaserBusy(true);
    try {
      var info = await connectLaser(handleLaserMeasurement, function() {
        setLaserConn(null);
        shotsRef.current = [];
        setLaserState(null);
        laserToast('Laser disconnected');
      });
      setLaserConn(info);
      laserToast('Connected to ' + info.brand + ' laser' + (info.beta ? ' (beta)' : ''));
    } catch (e) {
      var msg = (e && e.message) || String(e);
      if (!/cancel/i.test(msg)) laserToast('Laser: ' + msg);
    } finally {
      setLaserBusy(false);
    }
  }

  // ── EXCEL EXPORT with full formatting (ExcelJS) ──
  async function exportExcel() {
    if (dirty) { note('Please save the measurements first, then export.', 'error'); return; }
    var date = new Date().toLocaleDateString(appConfig.locale, { year:'numeric', month:'long', day:'numeric' });
    var dataRows = rows.map((row, idx) => {
      var calc = calculations[idx];
      if (!calc.hasData) return null;
      return {
        num: row.id,
        // PATCH oct20-export-inches: real numbers, no inch sign, so Excel formulas can use them
        width: row.w ? (parseFloat(row.w) || 0) + (FRAC_VALS[row.wf] || 0) : '',
        height: row.h ? (parseFloat(row.h) || 0) + (FRAC_VALS[row.hf] || 0) : '',
        rw: calc.rw,
        rh: calc.rh,
        sqft: calc.sqft,
        isMin: calc.isMin ? 'Yes' : '',
      };
    }).filter(Boolean);

    var wb = new ExcelJS.Workbook();
    var ws = wb.addWorksheet('Measurements');

    ws.columns = [ // PATCH oct23-measure: no rounded columns, column A wide enough for labels
      { width: 17 },
      { width: 14 },
      { width: 16 },
      { width: 12 },
      { width: 10 },
    ];

    var tealArgb = { argb: 'FF2F6F6B' }; // PATCH oct21-ui-measure
    var lightTeal = { argb: 'FFE6F0EF' };
    var white = { argb: 'FFFFFFFF' };
    var grey = { argb: 'FFCCCCCC' };
    var thinGrey = { style: 'thin', color: grey };

    // Company name
    var titleRow = ws.addRow([appConfig.companyFullName || 'Window Measurements']);
    titleRow.getCell(1).font = { bold: true, size: 14, color: tealArgb };
    ws.mergeCells(titleRow.number, 1, titleRow.number, 5);

    ws.addRow(['Date:', date]);
    if (customer) ws.addRow(['Customer:', customer.full_name, '', 'Area:', customer.area || '']);
    if (jobDesc) ws.addRow(['Job:', jobDesc]);
    ws.addRow([]);

    // Header row
    var headerRow = ws.addRow(['#', 'Width (in)', 'Height (in)', 'Sq Ft', minUsed > 0 ? 'Min ' + minUsed + '?' : 'Min?']);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: white };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: tealArgb };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: thinGrey, bottom: thinGrey, left: thinGrey, right: thinGrey };
    });

    // Data rows
    dataRows.forEach((r, i) => {
      var row = ws.addRow([r.num, r.width, r.height, r.sqft, r.isMin]);
      var isEven = i % 2 === 0;
      row.eachCell((cell, colNum) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: thinGrey, bottom: thinGrey, left: thinGrey, right: thinGrey };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: isEven ? white : lightTeal };
        if (colNum === 2 || colNum === 3) cell.numFmt = '# ?/?';
        if (colNum === 4) cell.numFmt = '0.00';
      });
    });

    ws.addRow([]);

    var totalWindowsRow = ws.addRow(['', '', 'Total Windows:', totalWindows]);
    totalWindowsRow.getCell(3).font = { bold: true };
    totalWindowsRow.getCell(4).font = { bold: true };

    var totalSqFtRow = ws.addRow(['', '', 'Total Sq Ft:', totalSqFt]);
    totalSqFtRow.getCell(3).font = { bold: true };
    totalSqFtRow.getCell(4).font = { bold: true };
    totalSqFtRow.getCell(4).numFmt = '0.00';

    if (pricePerSqFt && parseFloat(pricePerSqFt) > 0) {
      var priceRow = ws.addRow(['', '', 'Price / Sq Ft:', parseFloat(pricePerSqFt)]);
      priceRow.getCell(3).font = { bold: true };
      priceRow.getCell(4).font = { bold: true };
      priceRow.getCell(4).numFmt = '$#,##0.00';

      var totalPriceRow = ws.addRow(['', '', 'Total Price:', totalPrice]);
      totalPriceRow.getCell(3).font = { bold: true };
      totalPriceRow.getCell(4).font = { bold: true, color: tealArgb };
      totalPriceRow.getCell(4).numFmt = '$#,##0.00';
    }

    ws.addRow([]);
    ws.addRow(['Rounding Mode:', roundMode === '6inch' ? 'Nearest 6 inches' : 'Nearest 1 foot']);
    ws.addRow(['Minimum Sq Ft:', minUsed > 0 ? minUsed : 'None']);

    var fileName = appConfig.measurementPrefix + '-' + new Date().toISOString().slice(0,10) + '.xlsx';

    if (Capacitor.isNativePlatform()) {
      // Android/iOS: write to temp file via Filesystem, then share via native Share sheet
      try {
        var base64Buffer = await wb.xlsx.writeBuffer();
        var bytes = new Uint8Array(base64Buffer);
        var binary = '';
        for (var i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        var base64Data = btoa(binary);

        var writeResult = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
        });

        await Share.share({
          title: fileName,
          url: writeResult.uri,
          dialogTitle: 'Save or Share Excel File',
        });
        note('File ready to save or share!');
      } catch (err) {
        note('Error: ' + err.message, 'error');
      }
    } else {
      // Web browser: standard blob download
      var buffer = await wb.xlsx.writeBuffer();
      var blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      note('Exported to Excel!');
    }
  }

  function shareWhatsApp() {
    var lines = ['📐 *Window Measurements*'];
    if (customer) lines.push('Customer: ' + customer.full_name);
    if (jobDesc)  lines.push('Job: ' + jobDesc);
    lines.push('Date: ' + new Date().toLocaleDateString(appConfig.locale));
    lines.push('');
    rows.forEach((row, idx) => {
      var calc = calculations[idx];
      if (!calc.hasData) return;
      var { w, h } = getDecimal(row);
      lines.push('#' + row.id + '  W: ' + toFracLabel(w) + '  H: ' + toFracLabel(h) + '  →  ' + calc.sqft.toFixed(2) + ' sq ft' + (calc.isMin?' (min)':''));
    });
    lines.push(''); lines.push('*Total Windows: ' + totalWindows + '*'); lines.push('*Total Sq Ft: ' + totalSqFt.toFixed(2) + '*');
    if (totalPrice > 0) lines.push('*Total Price: $' + totalPrice.toFixed(2) + '*');
    window.open('https://wa.me/?text=' + encodeURIComponent(lines.join('\n')), '_blank');
  }

  // ── SAVE / UPDATE ──
  async function saveRecord() {
    setSaving(true);
    var rowsData = rows.map((r, i) => {
      var { w, h } = getDecimal(r); var calc = calculations[i];
      return {
        num: i + 1,
        width_whole: r.w, width_frac: r.wf,
        height_whole: r.h, height_frac: r.hf,
        width_decimal: w, height_decimal: h,
        rounded_width: calc.rw, rounded_height: calc.rh,
        sqft: calc.hasData ? calc.sqft : 0,
        is_minimum: calc.isMin,
      };
    }).filter(r => r.width_decimal > 0 && r.height_decimal > 0);

    var payload = {
      contact_id:     customer ? customer.id        : null,
      customer_name:  customer ? customer.full_name : null,
      company_id:     appConfig.companyId,
      job_description: jobDesc,
      rows:           rowsData,
      total_sqft:     totalSqFt,
      total_windows:  totalWindows,
      price_per_sqft: parseFloat(pricePerSqFt) || 0,
      total_price:    totalPrice,
      rounding_mode:  roundMode,
      min_sqft:       minUsed,
    };

    var error;
    if (editingRecord && editingRecord.id) {
      // UPDATE existing record — do NOT overwrite created_at
      var result = await supabase.from('measurements').update(payload).eq('id', editingRecord.id);
      error = result.error;
    } else {
      // INSERT new record
      var result = await supabase.from('measurements').insert([{ ...payload, created_at: new Date().toISOString() }]); // lint-ignore-company-id — payload already includes company_id, built above
      error = result.error;
    }

    setSaving(false);
    if (error) { note('Error saving measurements', 'error'); return; }
    setDirty(false);
    note(editingRecord
      ? 'Measurements updated! You can still export or close.'
      : 'Measurements saved! You can still export or close.'
    );
  }

  var date = fmtDate(new Date());

  function LaserDots({ count }) {
    var needed = shotsNeeded();
    return (
      <div style={{ display:'flex', gap:4 }}>
        {Array.from({length:needed}).map((_,i) => (
          <div key={i} style={{ width:10, height:10, borderRadius:'50%', background: count>i ? teal : 'transparent', border:'1.5px solid '+(count>i?teal:inputBorder) }} />
        ))}
      </div>
    );
  }

  function seg(options, value, onPick, disabled) {
    return (
      <div style={{ display:'flex', padding:3, background:sectionBg, border:'1px solid '+inputBorder, borderRadius:10, opacity: disabled ? 0.5 : 1 }}>
        {options.map(o => {
          var on = value === o.key;
          return (
            <button key={o.key} onClick={() => onPick(o.key)} aria-pressed={on}
              style={{ flex:1, minHeight:40, borderRadius:8, border:'none', background: on ? teal : 'transparent', color: on ? 'white' : textColor, cursor:'pointer', fontFamily:'inherit', padding:'4px 6px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:14, fontWeight:600 }}>{o.label}</span>
              {o.sub && <span style={{ fontSize:12, opacity: on ? 0.9 : 0.75 }}>{o.sub}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  var tips = isPhone ? [
    'After you type a number, it jumps to the fraction box by itself',
    'The x on a row clears it',
    '"min" means the minimum sq ft was charged for that window',
    'With the laser connected, each shot fills the next box',
  ] : [
    'Tab moves to the next box',
    'In the fraction list, the arrow keys change the value and stay in the box',
    'Enter moves to the next box',
    'Alt + Down arrow opens the fraction list',
  ];
  var hasRows = totalWindows > 0;
  var GRID = '24px 1fr 1fr 50px 32px';

  return (
    <div style={{ fontFamily:'inherit', maxWidth:600, margin:'0 auto', padding:16, background: theme ? theme.bg : '#F3F5F4', minHeight:'100vh', paddingBottom:40 }}>
      {/* PATCH oct21-ui-measure */}

      {/* Header */}
      <div style={{ background:cardBg, border:'1px solid '+borderCol, borderRadius:14, padding:'10px 14px', marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={onClose} aria-label="Back"
          style={{ minHeight:44, padding:'0 12px', background:sectionBg, color:textColor, border:'1px solid '+borderCol, borderRadius:10, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:15, fontWeight:600, fontFamily:'inherit' }}>
          <MIcon name="arrowLeft" size={18} /> Back
        </button>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:22, fontWeight:700, color:textColor, lineHeight:1.2 }}>Measurements</div>
          <div style={{ fontSize:13, color:mutedText }}>{date}</div>
        </div>
      </div>

      {editingRecord && (
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:subText, background:sectionBg, border:'1px solid '+borderCol, borderRadius:10, padding:'8px 12px', marginBottom:12 }}>
          <MIcon name="pencil" size={16} />
          <span>Editing saved measurement · {fmtDate(editingRecord.created_at)}{editingRecord.job_description ? ' · ' + editingRecord.job_description : ''}</span>
        </div>
      )}

      {/* Customer and job */}
      <div style={CARD}>
        <label htmlFor="m-customer" style={LBL}>Customer (optional)</label>
        {customer ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', minHeight:44, background:tealTint, borderRadius:10, padding:'0 6px 0 12px', marginBottom:12 }}>
            <span style={{ fontSize:15, fontWeight:600, color:tealText }}>{customer.full_name}</span>
            <button aria-label="Remove customer" onClick={() => setCustomer(null)} style={{ width:36, height:36, background:'transparent', border:'none', color:tealText, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MIcon name="x" size={16} />
            </button>
          </div>
        ) : (
          <CustomerSearch onSelect={setCustomer} theme={theme} C={C} />
        )}
        <label htmlFor="m-job" style={LBL}>Job or room</label>
        <input id="m-job" placeholder="e.g. Living room, 2nd floor" value={jobDesc} onChange={e => setJobDesc(e.target.value)} style={FIELD} />
      </div>

      {/* Laser */}
      <div style={CARD}>
        <div style={{ fontSize:15, fontWeight:600, color:textColor, marginBottom:2 }}>Laser</div>
        <div style={{ fontSize:13, color:mutedText, marginBottom:12 }}>Bosch GLM CG series and Leica DISTO (beta) by Bluetooth</div>
        {!laserAllowed && <div style={{ fontSize:13, color:subText, fontWeight:600, marginBottom:12 }}>{laserBlockedMsg}</div>}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
          <button onClick={handleConnectLaser} disabled={laserBusy}
            style={Object.assign({}, BTN_S, { cursor: laserBusy ? 'wait' : 'pointer', opacity: (laserBusy || !laserAllowed) ? 0.5 : 1 })}>
            <MIcon name="bluetooth" size={18} />
            {laserBusy ? 'Connecting...' : laserConn ? 'Disconnect laser' : 'Connect laser'}
          </button>
          {laserConn && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:tealText, fontWeight:600 }}>
              <span style={{ width:8, height:8, borderRadius:99, background:teal }}></span>
              {laserConn.brand}{laserConn.beta ? ' (beta)' : ''} · Next: row {laserTarget.row + 1} {laserTarget.field === 'w' ? 'width' : 'height'}
            </span>
          )}
        </div>
        {seg([{key:'single',label:'Single',sub:'1 shot'},{key:'average',label:'Average',sub:'3 shots'},{key:'smallest',label:'Smallest',sub:'3 shots'}], laserMode, (k) => { setLaserMode(k); setLaserState(null); shotsRef.current = []; })}
        <div style={{ fontSize:13, color:mutedText, marginTop:8 }}>
          {laserMode==='single' && 'One shot fills the box.'}
          {laserMode==='average' && 'Three shots, the average is used.'}
          {laserMode==='smallest' && 'Three shots, the smallest is used (usual for inside mount).'}
        </div>
        {laserState && (
          <div style={{ marginTop:10, background:sectionBg, border:'1px solid '+borderCol, borderRadius:10, padding:'8px 12px', display:'flex', alignItems:'center', gap:10 }}>
            <LaserDots count={laserState.shots.length} />
            <span style={{ fontSize:13, color:textColor }}>Shot {laserState.shots.length} of {shotsNeeded()} · Row {laserState.rowIdx+1} {laserState.field==='w'?'width':'height'}</span>
            <button aria-label="Cancel shots" onClick={() => { setLaserState(null); shotsRef.current = []; }} style={{ marginLeft:'auto', width:36, height:36, background:'transparent', border:'none', color:mutedText, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MIcon name="x" size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Rounding */}
      <div style={CARD}>
        <div style={{ fontSize:15, fontWeight:600, color:textColor, marginBottom:10 }}>Rounding</div>
        {seg([{key:'6inch',label:'Nearest 6 in'},{key:'1foot',label:'Nearest 1 ft'}], roundMode, setRoundMode)}
        <div style={{ fontSize:13, color:mutedText, marginTop:8, lineHeight:1.5 }}>
          Width and height rounded up {roundMode==='6inch'?'to the next 6 in':'to the next foot'}, then sq ft rounded up {roundMode==='6inch'?'to the next 0.5':'to a whole number'}.
          {minUsed > 0 ? ' Minimum ' + minUsed + ' sq ft per window.' : ' No minimum per window.'} Total rounded up to a whole number.
        </div>
      </div>

      {/* Window sizes */}
      <div style={CARD}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
          <span style={{ fontSize:15, fontWeight:600, color:textColor }}>Window sizes</span>
          <button onClick={() => setShowTips(!showTips)} aria-label="Tips" aria-expanded={showTips}
            style={{ width:32, height:32, borderRadius:99, border:'none', background: showTips ? tealTint : 'transparent', color:tealText, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <MIcon name="info" size={18} />
          </button>
          <span style={{ marginLeft:'auto', fontSize:13, color:mutedText }}>inches</span>
        </div>
        {showTips && (
          <div style={{ background:sectionBg, border:'1px solid '+borderCol, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
            {tips.map((t, i) => (
              <div key={i} style={{ display:'flex', gap:8, fontSize:13, color:subText, lineHeight:1.5, padding:'2px 0' }}>
                <span style={{ color:tealText }}>•</span><span>{t}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:GRID, gap:4, marginBottom:6, paddingBottom:6, borderBottom:'1px solid '+borderCol }}>
          {['#','Width','Height','Sq ft',''].map((h,i) => (
            <span key={i} style={{ fontSize:13, color:mutedText, textAlign:'center', fontWeight:600 }}>{h}</span>
          ))}
        </div>

        {rows.map((row, idx) => {
          var calc = calculations[idx];
          var isActive = idx === activeRow;
          var rowBorder = isActive ? teal : inputBorder;

          return (
            <div key={row.id} onClick={() => setActiveRow(idx)}
              style={{ display:'grid', gridTemplateColumns:GRID, gap:4, alignItems:'center', marginBottom:6, padding:4, background: isActive ? tealTint : 'transparent', borderRadius:10, cursor:'pointer' }}>

              <div style={{ fontSize:13, fontWeight:600, color: isActive ? tealText : mutedText, textAlign:'center' }}>{row.id}</div>

              <div style={{ display:'flex', gap:3, minWidth:0 }}>
                <input ref={el=>{ inputRefs.current[idx+'-w']=el; }} type="number" inputMode="decimal" min="0" placeholder="0" value={row.w} aria-label={'Row ' + row.id + ' width'}
                  onFocus={handleFocus} onClick={()=>setActiveRow(idx)}
                  onChange={e=>handleInput(idx,'w',e.target.value)}
                  style={Object.assign({},IS,{flex:1, borderColor:rowBorder})} />
                <select ref={el=>{ inputRefs.current[idx+'-wf']=el; }} value={row.wf} aria-label={'Row ' + row.id + ' width fraction'}
                  onKeyDown={e=>fracKeyDown(e,idx,'wf')} onKeyUp={fracKeyUp} onMouseDown={fracMouseDown} onChange={e=>handleFracChange(idx,'wf',e.target.value)} onClick={()=>setActiveRow(idx)}
                  style={Object.assign({},SEL,{borderColor:rowBorder})}>
                  {FRACS.map(f=><option key={f} value={f}>{f||'—'}</option>)}
                </select>
              </div>

              <div style={{ display:'flex', gap:3, minWidth:0 }}>
                <input ref={el=>{ inputRefs.current[idx+'-h']=el; }} type="number" inputMode="decimal" min="0" placeholder="0" value={row.h} aria-label={'Row ' + row.id + ' height'}
                  onFocus={handleFocus} onClick={()=>setActiveRow(idx)}
                  onChange={e=>handleInput(idx,'h',e.target.value)}
                  style={Object.assign({},IS,{flex:1, borderColor:rowBorder})} />
                <select ref={el=>{ inputRefs.current[idx+'-hf']=el; }} value={row.hf} aria-label={'Row ' + row.id + ' height fraction'}
                  onKeyDown={e=>fracKeyDown(e,idx,'hf')} onKeyUp={fracKeyUp} onMouseDown={fracMouseDown} onChange={e=>handleFracChange(idx,'hf',e.target.value)} onClick={()=>setActiveRow(idx)}
                  style={Object.assign({},SEL,{borderColor:rowBorder})}>
                  {FRACS.map(f=><option key={f} value={f}>{f||'—'}</option>)}
                </select>
              </div>

              <div style={{ fontSize:14, fontWeight:600, textAlign:'center', color: calc.hasData ? textColor : mutedText }}>
                {calc.hasData ? calc.sqft.toFixed(2) : '—'}
                {calc.isMin && <div style={{ display:'inline-block', fontSize:11, fontWeight:600, color:subText, background:sectionBg, border:'1px solid '+borderCol, borderRadius:99, padding:'0 6px', lineHeight:'16px', marginTop:2 }}>min</div>}
              </div>

              <button tabIndex={-1} aria-label={'Clear row ' + row.id} onClick={e=>{ e.stopPropagation(); clearRow(idx); }} disabled={!calc.hasData && !row.w && !row.h}
                style={{ width:32, height:44, background:'transparent', border:'none', color: (calc.hasData || row.w || row.h) ? subText : borderCol, cursor: (calc.hasData || row.w || row.h) ? 'pointer' : 'default', padding:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <MIcon name="x" size={16} />
              </button>
            </div>
          );
        })}

        <button onClick={() => { addRow(); setActiveRow(rows.length); setTimeout(() => { var el = inputRefs.current[rows.length + '-w']; if (el) el.focus(); }, 80); }}
          style={Object.assign({}, BTN_S, { width:'100%', marginTop:4 })}>
          <MIcon name="plus" size={18} /> Add window
        </button>

        {/* Totals */}
        <div style={{ borderTop:'1px solid '+borderCol, marginTop:14, paddingTop:14, display:'flex', gap:8 }}>
          <div style={{ flex:1, background:sectionBg, border:'1px solid '+borderCol, borderRadius:10, padding:10, textAlign:'center' }}>
            <div style={{ fontSize:13, color:mutedText, marginBottom:2 }}>Total windows</div>
            <div style={{ fontSize:22, fontWeight:700, color:textColor }}>{totalWindows}</div>
          </div>
          <div style={{ flex:1, background:sectionBg, border:'1px solid '+borderCol, borderRadius:10, padding:10, textAlign:'center' }}>
            <div style={{ fontSize:13, color:mutedText, marginBottom:2 }}>Total sq ft</div>
            <div style={{ fontSize:22, fontWeight:700, color:tealText }}>{totalSqFt.toFixed(2)}</div>
          </div>
        </div>

        {/* Price per sq ft */}
        <label htmlFor="m-price" style={Object.assign({}, LBL, { marginTop:14 })}>Price per sq ft (optional)</label>
        <div style={{ position:'relative' }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:15, color:mutedText }}>$</span>
          <input id="m-price" type="number" inputMode="decimal" min="0" placeholder="e.g. 12.50" value={pricePerSqFt}
            onFocus={e => e.target.select()}
            onChange={e => setPricePerSqFt(e.target.value)}
            style={Object.assign({}, FIELD, { paddingLeft:26, fontSize:16, fontWeight:600 })} />
        </div>
        {totalPrice > 0 && (
          <div style={{ marginTop:10, background:tealTint, borderRadius:10, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ color:tealText, fontSize:15, fontWeight:600 }}>Total price</div>
            <div style={{ color:tealText, fontSize:22, fontWeight:700 }}>${totalPrice.toFixed(2)}</div>
          </div>
        )}
      </div>

      {notice && (
        <div role="status" style={{ background: notice.type === 'error' ? (dark ? '#3A1D1B' : '#FDECEA') : (dark ? '#16301F' : '#E5F4EA'), border:'1px solid '+(notice.type === 'error' ? (dark ? '#F0776B' : '#B42318') : (dark ? '#4CC38A' : '#1E7A45')), color: notice.type === 'error' ? (dark ? '#F0776B' : '#B42318') : (dark ? '#4CC38A' : '#1E7A45'), borderRadius:10, padding:'10px 14px', marginBottom:10, fontSize:14, fontWeight:600, textAlign:'center' }}>
          {notice.text}
        </div>
      )}

      {/* Save, then Export and WhatsApp */}
      <button onClick={saveRecord} disabled={saving || !hasRows}
        style={Object.assign({}, BTN_P, { width:'100%', marginBottom:8, opacity:(saving || !hasRows) ? 0.5 : 1, cursor:(saving || !hasRows) ? 'not-allowed' : 'pointer' })}>
        {saving ? 'Saving...' : editingRecord ? 'Update measurements' : 'Save measurements'}
      </button>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <button onClick={exportExcel} disabled={!hasRows}
          style={Object.assign({}, BTN_S, { flex:1, opacity: hasRows ? 1 : 0.5, cursor: hasRows ? 'pointer' : 'not-allowed' })}>
          <MIcon name="sheet" size={18} /> Export Excel
        </button>
        <button onClick={shareWhatsApp} disabled={!hasRows}
          style={Object.assign({}, BTN_S, { flex:1, opacity: hasRows ? 1 : 0.5, cursor: hasRows ? 'pointer' : 'not-allowed' })}>
          <span style={{ color: dark ? '#4CC38A' : '#1E7A45' }}><MIcon name="whatsapp" size={18} /></span> WhatsApp
        </button>
      </div>
    </div>
  );
}
